import React, { useState, useEffect } from 'react';
import { Heart, Brain, Eye, Star, Users, Target } from 'lucide-react';
import './About.css';

const About = () => {
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll events for navbar styling
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 20) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const algorithmFeatures = [
    {
      icon: <Brain size={32} />,
      title: "Mind",
      subtitle: "Vibe Quiz",
      description: "Our intuitive personality assessment understands your mental state and preferences, matching you with someone who truly gets you."
    },
    {
      icon: <Eye size={32} />,
      title: "Body", 
      subtitle: "Visual Compatibility",
      description: "Advanced technology analyzes attraction patterns to connect you with people you'll find genuinely appealing."
    },
    {
      icon: <Star size={32} />,
      title: "Soul",
      subtitle: "Guna Milan",
      description: "Ancient astrological wisdom meets modern dating, ensuring your souls are aligned for lasting harmony."
    }
  ];

  const principles = [
    {
      icon: <Heart size={28} />,
      title: "Aligned Revenue",
      description: "We only succeed when you find love. Pay us back when you find your person, not before. No subscriptions, no endless swiping fees."
    },
    {
      icon: <Target size={28} />,
      title: "Quality Over Quantity", 
      description: "One meaningful match at a time. No more endless scrolling through profiles you'll never message."
    },
    {
      icon: <Users size={28} />,
      title: "Real Connections",
      description: "Built for people seeking genuine, lasting relationships—not casual hookups or endless entertainment."
    }
  ];

  return (
    <div className="about-container">
      {/* Navigation */}
      <nav className={`about-navbar ${isScrolled ? 'scrolled' : ''}`}>
        <div className="container about-navbar-container">
          <div className="about-logo">
            our<span className="about-logo-bold">vibe</span>
          </div>
          
          <div className="about-nav-links">
            <a href="/" className="about-nav-link">Home</a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="about-hero">
        <div className="about-hero-glow"></div>
        <div className="container">
          <div className="about-hero-content">
            <h1 className="about-hero-title">
              About <span className="about-hero-gradient">Our Vibe</span>
            </h1>
            <p className="about-hero-subtitle">
              We're building dating differently. Our mission is simple: help you find a meaningful, lasting relationship through intelligence, intuition, and authentic connection.
            </p>
          </div>
        </div>
      </section>

      {/* Algorithm Section */}
      <section className="about-algorithm">
        <div className="container">
          <div className="about-section-header">
            <h2 className="about-section-title">Mind, Body, Soul Algorithm</h2>
            <p className="about-section-subtitle">
              Three dimensions of compatibility working together to find your perfect match
            </p>
          </div>
          
          <div className="about-algorithm-grid">
            {algorithmFeatures.map((feature, index) => (
              <div key={index} className="about-algorithm-card">
                <div className="about-algorithm-icon">
                  {feature.icon}
                </div>
                <h3 className="about-algorithm-title">{feature.title}</h3>
                <h4 className="about-algorithm-subtitle">{feature.subtitle}</h4>
                <p className="about-algorithm-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Principles Section */}
      <section className="about-principles">
        <div className="container">
          <div className="about-section-header">
            <h2 className="about-section-title">How We're Different</h2>
            <p className="about-section-subtitle">
              We believe dating apps should work for you, not against you
            </p>
          </div>
          
          <div className="about-principles-grid">
            {principles.map((principle, index) => (
              <div key={index} className="about-principle-card">
                <div className="about-principle-icon">
                  {principle.icon}
                </div>
                <h3 className="about-principle-title">{principle.title}</h3>
                <p className="about-principle-description">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="about-mission">
        <div className="container">
          <div className="about-mission-content">
            <h2 className="about-mission-title">Our Vision</h2>
            <p className="about-mission-text">
              We envision a world where finding love isn't a game of chance or endless swiping. 
              Where technology serves genuine human connection, not engagement metrics. 
              Where your success is our success, and meaningful relationships are the ultimate goal.
            </p>
            <div className="about-mission-cta">
              <a href="/" className="about-cta-button">
                Join Our Vibe
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="about-footer">
        <div className="container about-footer-container">
          <div className="about-footer-logo-container">
            <div className="about-footer-logo">
              our<span className="about-footer-logo-bold">vibe</span>
            </div>
          </div>
          <div className="about-copyright">
            &copy; {new Date().getFullYear()} Our Vibe
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;